// Shared interaction layer for the SRS grid modules (training + learning):
//   - showSRSModal : the SRS-style error / question dialog
//   - installSRSFit: the auto-fit zoom for the custom (non-A3) canvas
//   - installSRSClicks: the maneuver click handler with all NTE interlocks
//
// Extracted from HighTensionTraining.tsx so both modules operate the station
// under exactly the same rules; the module-specific behaviour (logging, scoring,
// the learning module's "only the requested switch may be operated" rule) is
// injected through callbacks.

import SCHEME_ELEMENTS from "./getElements";
import zoomFunc from "./zoomFunction";
import { Maneuvers } from "./srsScheme";
import { Severity } from "./htScenarios";

const S: any = SCHEME_ELEMENTS;

// SRS-style modal: errors show a single "Ok" button; questions (onConfirm
// given) show "Da" / "Nu" and proceed only on "Da".
export const showSRSModal = (msg: string, onConfirm: (() => void) | null = null) => {
    document.querySelectorAll(".ht-modal-overlay").forEach((m) => m.remove());
    const overlay = document.createElement("div");
    overlay.className = "ht-modal-overlay";
    const box = document.createElement("div");
    box.className = "ht-modal";
    const text = document.createElement("p");
    text.textContent = msg;
    box.appendChild(text);
    const buttons = document.createElement("div");
    buttons.className = "ht-modal-buttons";
    const addButton = (label: string, cls: string, action: (() => void) | null) => {
        const btn = document.createElement("button");
        btn.type = "button";
        btn.className = cls;
        btn.textContent = label;
        // NOTE: no stopPropagation — the click must reach the window "click"
        // listener that re-applies the custom HT fit; otherwise the scheme stays
        // at the clobbered A3 size (looks separated) until the next click. The
        // maneuver handler ignores it (the button is not inside a .cell).
        btn.onclick = () => { overlay.remove(); if (action) action(); };
        buttons.appendChild(btn);
    };
    if (onConfirm) { addButton("Da", "", onConfirm); addButton("Nu", "", null); }
    else addButton("Ok", "ok", null);
    box.appendChild(buttons);
    overlay.appendChild(box);
    document.body.appendChild(overlay);
};

/**
 * Auto-fit: the scheme lives on a custom cols x rows canvas (each cell ~10mm)
 * chosen so its aspect ratio ~= 16:9, filling the page on wide screens.
 * zoomFunc sizes cells/labels from the zoom factor; we then override the page +
 * container to the custom canvas size (zoomFunc hardcodes A3 420x297mm).
 * Re-applied on window resize and after every click (a freshly swapped sign
 * image has to be sized too). Returns the cleanup function.
 */
export const installSRSFit = (cols: number, rows: number): (() => void) => {
    const pageWmm = cols * 10;
    const pageHmm = rows * 10;
    const applyZoom = (zoom: number) => {
        document.body.setAttribute("zoom", zoom.toString());
        zoomFunc(zoom, false, "A3");
        const w = `${pageWmm * zoom}mm`, h = `${pageHmm * zoom}mm`;
        document.querySelectorAll<HTMLElement>("body.ht-scheme .page-container").forEach((p) => {
            p.style.width = p.style.minWidth = p.style.maxWidth = w;
            p.style.height = p.style.minHeight = p.style.maxHeight = h;
            // Force the page to sit horizontally centred in the viewport,
            // regardless of the ancestor layout.
            const parent = p.parentElement;
            if (parent) {
                parent.style.display = "flex";
                parent.style.justifyContent = "center";
                parent.style.width = "100%";
            }
        });
        document.querySelectorAll<HTMLElement>("body.ht-scheme .grid-total-container").forEach((c) => {
            c.style.height = h;
        });
    };
    const fit = () => {
        const MM_TO_PX = 96 / 25.4;
        // The slim scheme bar sits over the top of the viewport, so the height
        // it occupies is not available to the sheet.
        const barH = document.body.classList.contains("has-scheme-bar")
            ? parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--scheme-bar-h")) || 48
            : 0;
        const widthZoom = (window.innerWidth - 16) / (pageWmm * MM_TO_PX);
        const heightZoom = (window.innerHeight - 16 - barH) / (pageHmm * MM_TO_PX);
        const zoom = Math.max(0.3, Math.round(Math.min(widthZoom, heightZoom) * 100) / 100);
        applyZoom(zoom);
    };
    // On any click (sign toggle, zoom buttons) re-apply at the CURRENT zoom so
    // swapped signs get sized without clobbering a manual zoom-in/out level.
    const reapply = () => applyZoom(Number(document.body.getAttribute("zoom")) || 1);
    const timer = setTimeout(fit, 50);
    window.addEventListener("resize", fit);
    window.addEventListener("click", reapply);
    return () => {
        clearTimeout(timer);
        window.removeEventListener("resize", fit);
        window.removeEventListener("click", reapply);
    };
};

// What the user just operated. `kind` is the equipment family, `what` the RO
// noun used in logs ("separator de bara IB", "intreruptor", ...), `act` the RO
// verb ("Inchidere" / "Deschidere" / "Pornire" / "Oprire").
export interface SwitchInfo {
    x: number;
    y: number;
    name: string;                 // raw sign name
    closing: boolean;             // the click closes / starts it
    kind: "clp" | "separator" | "breaker" | "generator";
    what: string;
    act: string;
    barLine?: string;             // busbar of a bar separator (IB / IIA / ...)
}

export interface ClickHooks {
    useStore: any;
    swapSignImage: (x: number, y: number, page: number, img: any) => void;
    maneuvers: () => Maneuvers;
    // Called before any interlock runs. Return a message to refuse the maneuver
    // (used by the learning module: only the switch the dispatcher asked for may
    // be operated), or null to let it through.
    guard?: (info: SwitchInfo) => string | null;
    // A maneuver actually went through.
    onManeuver?: (info: SwitchInfo) => void;
    // An interlock refused the maneuver (or `guard` did, with severity "minor").
    onWarning?: (severity: Severity, category: string, msg: string, x: number, y: number) => void;
}

/**
 * Makes the HT generator / separator / CLP / intreruptor signs actionable:
 * a click toggles their state after every applicable interlock passed.
 * getElectricity re-runs on any sign change (Grid effect dep [signs]) so the
 * current recolours by itself. Returns the cleanup function.
 */
export const installSRSClicks = (hooks: ClickHooks): (() => void) => {
    const { useStore, swapSignImage, maneuvers } = hooks;

    const onClick = (e: any) => {
        const cellEl = e.target?.closest?.(".cell");
        if (!cellEl) return;
        // NOTE: GridElement writes the attributes swapped ("row" holds the
        // column index and "column" the row index) — store x is "row" here.
        const x = Number(cellEl.getAttribute("row"));
        const y = Number(cellEl.getAttribute("column"));
        if (Number.isNaN(x) || Number.isNaN(y)) return;
        const els = (useStore.getState?.().elements) || [];
        const sign = els.find((s: any) => s.x === x && s.y === y && s.page === 1);
        const nm: string = sign?.names?.[0]?.name || "";
        if (!nm || !nm.includes("inalta")) return;
        // Only the four operable families are switches. The scheme carries other
        // high-tension symbols whose names also contain "inalta" — the consumer
        // (consumator_inalta_tensiune), the measuring cell and the short
        // conductor — and the `kind` classification below ends in an unguarded
        // "else generator", so a click on any of them used to be treated as a
        // generator and swapped its image for one. They are inert.
        if (!/separator|intreruptor|generator/.test(nm)) return;

        const findName = (cx: number, cy: number): string =>
            els.find((s: any) => s.x === cx && s.y === cy && s.page === 1)?.names?.[0]?.name || "";
        const isClosed = (cell: [number, number] | undefined, type: string) =>
            !!cell && findName(cell[0], cell[1]).includes(type) && findName(cell[0], cell[1]).includes("inchis");
        // Voltage check straight off the rendered grid (attributes swapped, see above).
        const cellHasCurrent = (cx: number, cy: number) => {
            const el = Array.from(document.querySelectorAll(".cell")).find((c: any) =>
                Number(c.getAttribute("row")) === cx && Number(c.getAttribute("column")) === cy);
            return !!el?.querySelector("img.has-current");
        };
        const showModal = (msg: string, onConfirm: (() => void) | null = null, severity: Severity | null = null, category = "") => {
            // Record the interlock violation (errors, not the parallel question).
            if (severity && !onConfirm) hooks.onWarning?.(severity, category, msg, x, y);
            showSRSModal(msg, onConfirm);
        };

        const M = maneuvers();
        const closing = nm.includes("deschis");
        const key = `${x}_${y}`;
        // A bar separator names its busbar (IA/IB/IIA/IIB); anything else is a
        // line separator.
        const barLine = M.sepBarLine[key];
        const sepDesc = barLine ? `separator de bară ${barLine}` : "separator de linie";
        const kind: SwitchInfo["kind"] = nm.includes("separator_clp") ? "clp"
            : nm.includes("separator") ? "separator"
            : nm.includes("intreruptor") ? "breaker" : "generator";
        const what = kind === "clp" ? "CLP" : kind === "separator" ? sepDesc
            : kind === "breaker" ? "întreruptor" : "generator";
        const act = kind === "generator" ? (nm.includes("pornit") ? "Oprire" : "Pornire") : (closing ? "Închidere" : "Deschidere");
        const info: SwitchInfo = { x, y, name: nm, closing, kind, what, act, barLine };

        // Module-level veto (learning: wrong switch for the current step).
        const refusal = hooks.guard?.(info);
        if (refusal) { showModal(refusal); return; }

        // Ordering checks shared by separators and breakers.
        const orderBlocked = (): boolean => {
            if (closing) {
                const req = M.closeRequires[key];
                if (req) {
                    const closedOnes = req.cells.map((cell) => isClosed(cell, "inalta"));
                    const ok = req.all ? closedOnes.every(Boolean) : closedOnes.some(Boolean);
                    if (!ok) { showModal(req.msg); return true; }
                }
            } else {
                const req = M.openRequires[key];
                if (req && req.cells.some((cell) => isClosed(cell, "inalta"))) {
                    showModal(req.msg, null, "major", "wrong-order");
                    return true;
                }
            }
            return false;
        };

        if (kind === "clp") {
            // CLP may only be closed on a dead path...
            if (closing && cellHasCurrent(x, y - 1)) {
                showModal("Nu se poate închide CLP-ul dacă linia este sub tensiune!", null, "critical", "clp-under-voltage");
                return;
            }
            const clpBar = M.clpBarLine[key];
            if (clpBar) {
                // C.M. busbar earthing: every bar separator that taps this busbar
                // section (IB / IA / IIB / IIA) must be OPEN before grounding it.
                if (closing && (M.barLineSeps[clpBar] || []).some((cell) => findName(cell[0], cell[1]).includes("inchis"))) {
                    showModal(`Nu se poate închide CLP-ul de la celula de măsură ${clpBar} pentru că există separatoare închise pe sistemul de bară ${clpBar}!`, null, "major", "busbar-earthing");
                    return;
                }
            } else {
                // Generator / consumer bay earthing: only the bay itself must be
                // visibly separated (line separator, bar separators AND breaker open).
                if (closing && (M.clpIsolators[key] || []).some((cell) => findName(cell[0], cell[1]).includes("inchis"))) {
                    showModal("Nu se poate închide CLP-ul: separatorul de linie, separatoarele de bară și întreruptorul trebuie să fie deschise (stare separat vizibilă)!", null, "major", "clp-not-separated");
                    return;
                }
            }
            swapSignImage(x, y, 1, closing
                ? S["separator_clp_inalta_tensiune_inchis"].imageSource
                : S["separator_clp_inalta_tensiune_deschis"].imageSource);
            hooks.onManeuver?.(info);
        } else if (kind === "separator") {
            // Separators never operate under load: bay breaker must be open.
            const breakers = M.breakers[key] || [];
            if (breakers.some((cell) => isClosed(cell, "intreruptor"))) {
                showModal("Nu se poate manevra separatorul dacă întreruptorul este închis (sub sarcină)!", null, "critical", "separator-under-load");
                return;
            }
            if (closing && isClosed(M.clp[key], "separator_clp")) {
                showModal("Nu se poate închide separatorul dacă CLP-ul este închis (linia este legată la pământ)!", null, "major", "sep-while-clp");
                return;
            }
            // Vice versa (busbar earthing): can't close a bar separator whose busbar
            // section is earthed by its C.M. CLP.
            if (closing && barLine && isClosed(M.barLineClp[barLine], "separator_clp")) {
                showModal(`Nu se poate închide separatorul de bară ${barLine} pentru că CLP-ul de la celula de măsură ${barLine} este închis!`, null, "major", "busbar-earthing");
                return;
            }
            // One bay may sit on only one busbar at a time.
            const pair = M.pair[key];
            if (closing && pair && isClosed(pair.cell, "separator")) {
                showModal(pair.msg, null, "critical", "both-bar-seps");
                return;
            }
            if (orderBlocked()) return;
            swapSignImage(x, y, 1, closing
                ? S["separator_inalta_tensiune_inchis"].imageSource
                : S["separator_inalta_tensiune_deschis"].imageSource);
            hooks.onManeuver?.(info);
        } else if (kind === "breaker") {
            if (closing && isClosed(M.clp[key], "separator_clp")) {
                showModal("Nu se poate închide întreruptorul dacă CLP-ul este închis (linia este legată la pământ)!", null, "major", "breaker-while-clp");
                return;
            }
            if (orderBlocked()) return;
            const doSwap = () => {
                swapSignImage(x, y, 1, closing
                    ? S["intreruptor_inalta_tensiune_inchis"].imageSource
                    : S["intreruptor_inalta_tensiune_deschis"].imageSource);
                hooks.onManeuver?.(info);
            };
            if (closing) {
                // Both sides already energized -> this closes a parallel between
                // two sources; ask for the parallel conditions.
                const horizontal = (((sign?.flip || 0) + (sign?.names?.[0]?.flip || 0)) % 180) === 90;
                const sideA = horizontal ? cellHasCurrent(x - 1, y) : cellHasCurrent(x, y - 1);
                const sideB = horizontal ? cellHasCurrent(x + 1, y) : cellHasCurrent(x, y + 1);
                if (sideA && sideB) {
                    showModal(M.parallelMsg[key] || "Sunt îndeplinite condițiile de paralel?", doSwap);
                    return;
                }
            }
            doSwap();
        } else {
            if (!nm.includes("pornit") && isClosed(M.clp[key], "separator_clp")) {
                showModal("Nu se poate porni generatorul dacă CLP-ul este închis (linia este legată la pământ)!", null, "major", "gen-clp");
                return;
            }
            swapSignImage(x, y, 1, nm.includes("pornit")
                ? S["generator_inalta_tensiune"].imageSource
                : S["generator_inalta_pornit"].imageSource);
            hooks.onManeuver?.(info);
        }
    };

    document.addEventListener("click", onClick);
    return () => document.removeEventListener("click", onClick);
};
